/**
 * tokens-adapter — reads `.appostle/brand/tokens.json` (W3C DTCG format) and
 * converts it into the same `BrandEntry[]` shape that the `.md` scanner produces.
 *
 * Priority rule: callers check for `tokens.json` first and fall back to `.md`
 * scanning only when the file is absent. The adapter never writes anything; it
 * is read-only.
 *
 * Section remapping table:
 * The adapter surfaces exactly 9 sections:
 *   components ← button.*, pill.*, iconbutton.*
 *   colors     ← color.*, token.{bg,fg,accent,border,status}.*
 *   icons      ← icon.*
 *   layout     ← prose only (no token namespace — body from prose/layout.md)
 *   spacing    ← spacing.* (container, breakpoints, section-padding, whitespace)
 *   logo       ← brand.logo.*
 *   motion     ← motion.*
 *   shadows    ← shadow.*
 *   shapes     ← brand.shape.*
 *   typography ← typeface.*, token.{h1-h6,body-base,body-small,caption,button,label}.*
 */

import fs from "node:fs/promises";
import path from "node:path";

import type { BrandEntry, BrandScope, BrandVariable, BrandVariableType } from "./scanner.js";

// ---------------------------------------------------------------------------
// Internal types for the W3C DTCG token shape
// ---------------------------------------------------------------------------

interface TokenExtensionsAppostle {
  weight?: number;
  locked?: boolean;
  enabled?: boolean;
  options?: string[];
  fileRef?: string;
  fontProvider?: string;
}

interface TokenNode {
  $type?: string;
  $value?: unknown;
  $description?: string;
  $extensions?: {
    ohlord?: {
      appostle?: TokenExtensionsAppostle;
    };
  };
  [child: string]: unknown;
}

// ---------------------------------------------------------------------------
// Section definition — drives the remapping table
// ---------------------------------------------------------------------------

interface SectionDef {
  /** Sidebar section ID (= old .md name, no extension). */
  id: string;
  /** Top-level token namespaces to include in full (e.g. "button", "icon"). */
  namespaces?: string[];
  /**
   * Sub-keys under the `token.*` namespace to include.
   * E.g. ["bg", "fg"] pulls token.bg.* and token.fg.* into this section.
   */
  tokenSubKeys?: string[];
  /**
   * Separator between the token sub-key and its children in emitted variable
   * keys. Defaults to "." (e.g. `token.h1.size`). Use "-" for sections where
   * the preview expects hyphen-joined keys like `token.bg-base` (colors).
   */
  tokenSubKeyJoiner?: "." | "-";
  /**
   * Rename map for token sub-keys: tokens.json key → emitted key prefix.
   * Reserved for future divergence between on-disk shape and UI keys.
   */
  tokenSubKeyAliases?: Record<string, string>;
  /**
   * Sub-keys under the `brand.*` namespace to include.
   * E.g. ["logo"] pulls brand.logo.* into this section.
   */
  brandSubKeys?: string[];
}

const SECTION_DEFS: SectionDef[] = [
  { id: "components", namespaces: ["button", "pill", "iconbutton"] },
  {
    id: "colors",
    namespaces: ["color"],
    // colors-preview.tsx filters by prefix "token.bg-", "token.fg-", etc.
    // (hyphen separator), so we join sub-key children with "-" not ".".
    tokenSubKeys: ["bg", "fg", "accent", "border", "status"],
    tokenSubKeyJoiner: "-",
  },
  { id: "icons", namespaces: ["icon"] },
  { id: "layout" },
  { id: "spacing", namespaces: ["spacing"] },
  { id: "logo", brandSubKeys: ["logo"] },
  { id: "motion", namespaces: ["motion"] },
  { id: "shadows", namespaces: ["shadow"] },
  { id: "shapes", brandSubKeys: ["shape"] },
  {
    id: "typography",
    namespaces: ["typeface"],
    tokenSubKeys: [
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "body-base",
      "body-small",
      "caption",
      "button",
      "label",
    ],
  },
];

// ---------------------------------------------------------------------------
// $type → BrandVariableType mapping
// ---------------------------------------------------------------------------

function mapType(
  dtcgType: string | undefined,
  extensions: TokenExtensionsAppostle | undefined,
): BrandVariableType {
  // If the token declares options, treat as select regardless of $type.
  if (extensions?.options && extensions.options.length > 0) return "select";

  switch (dtcgType) {
    case "color":
      return "color";
    case "gradient":
      return "gradient";
    case "fontFamily":
      return "font";
    case "asset":
      return "asset";
    case "number":
      return "number";
    default:
      // dimension, string, shadow, cubicBezier, duration → text
      return "text";
  }
}

// ---------------------------------------------------------------------------
// Value serialisation
// ---------------------------------------------------------------------------

function serializeValue(raw: unknown): string {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (Array.isArray(raw)) return JSON.stringify(raw);
  if (typeof raw === "object") return JSON.stringify(raw);
  return String(raw);
}

// ---------------------------------------------------------------------------
// W3C alias resolution  {color.primary} → concrete value
// ---------------------------------------------------------------------------

const ALIAS_RE = /^\{([^}]+)\}$/;

function resolveAlias(value: string, root: Record<string, unknown>): string {
  const m = ALIAS_RE.exec(value);
  if (!m) return value;

  const parts = m[1].split(".");
  let cursor: unknown = root;
  for (const part of parts) {
    if (!cursor || typeof cursor !== "object") return value;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  if (!cursor || typeof cursor !== "object") return value;
  const node = cursor as TokenNode;
  if (node.$value !== undefined) {
    const resolved = serializeValue(node.$value);
    // Recursively resolve if the resolved value is itself an alias.
    if (ALIAS_RE.test(resolved)) return resolveAlias(resolved, root);
    return resolved;
  }
  return value;
}

// ---------------------------------------------------------------------------
// Recursive flattener
// ---------------------------------------------------------------------------

/**
 * Flattens a DTCG token group into a flat list of BrandVariables.
 *
 * @param obj    The token group object to flatten.
 * @param prefix Dot-path prefix accumulated so far (e.g. "color.primary").
 * @param root   Full tokens.json root — used for alias resolution.
 * @param synthesizeColorMeta  When true, emits synthetic .weight child
 *   variables for color tokens that carry that metadata in their Appostle
 *   extensions. This restores the legacy `color.primary.weight` key shape
 *   expected by palette-balance.tsx.
 */
function flattenNamespace(
  obj: Record<string, unknown>,
  prefix: string,
  root: Record<string, unknown>,
  synthesizeColorMeta = false,
): BrandVariable[] {
  const vars: BrandVariable[] = [];

  for (const [key, val] of Object.entries(obj)) {
    // Skip DTCG metadata keys.
    if (key.startsWith("$")) continue;

    if (!val || typeof val !== "object" || Array.isArray(val)) continue;

    const node = val as TokenNode;
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (node.$value !== undefined) {
      // Leaf token.
      const extensions = node.$extensions?.ohlord?.appostle;
      const rawValue = serializeValue(node.$value);
      const resolvedValue =
        typeof node.$value === "string" ? resolveAlias(rawValue, root) : rawValue;

      const varType = mapType(node.$type, extensions);

      const variable: BrandVariable = {
        key: fullKey,
        type: varType,
        label: key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        value: resolvedValue,
      };

      if (extensions?.options) variable.options = extensions.options;
      if (extensions?.locked !== undefined) variable.locked = extensions.locked;
      if (extensions?.enabled !== undefined) variable.enabled = extensions.enabled;

      // For asset tokens, fileRef lives in $value (the path string) rather
      // than in extensions. Fall back to extensions.fileRef if present.
      if (varType === "asset") {
        variable.fileRef =
          extensions?.fileRef ?? (resolvedValue.includes("/") ? resolvedValue : undefined);
      } else if (extensions?.fileRef) {
        variable.fileRef = extensions.fileRef;
      }

      if (extensions?.fontProvider === "google" || extensions?.fontProvider === "local") {
        variable.fontProvider = extensions.fontProvider;
      }

      // Check for sibling children (leaf + group hybrid nodes).
      const childKeys = Object.keys(node).filter((k) => !k.startsWith("$"));
      if (childKeys.length > 0) {
        vars.push(
          ...flattenNamespace(node as Record<string, unknown>, fullKey, root, synthesizeColorMeta),
        );
      } else {
        vars.push(variable);

        // Synthesize a .weight sibling variable for palette entries
        // (color + gradient). It was a standalone BrandVariable key in the
        // legacy .md shape (e.g. `color.primary.weight`) but lives inside
        // extensions in tokens.json. palette-balance.tsx looks it up by key.
        // Both color and gradient entries live under the dynamic `color.*`
        // namespace and participate in the balance cluster.
        if (synthesizeColorMeta && (varType === "color" || varType === "gradient") && extensions) {
          if (extensions.weight !== undefined) {
            vars.push({
              key: `${fullKey}.weight`,
              type: "number",
              label: `${variable.label} Weight`,
              value: String(extensions.weight),
            });
          }
        }
      }
    } else {
      // Group node — recurse.
      vars.push(
        ...flattenNamespace(node as Record<string, unknown>, fullKey, root, synthesizeColorMeta),
      );
    }
  }

  return vars;
}

// ---------------------------------------------------------------------------
// Section builder — assembles variables for one section def
// ---------------------------------------------------------------------------

function buildSectionVariables(def: SectionDef, root: Record<string, unknown>): BrandVariable[] {
  const vars: BrandVariable[] = [];
  const synthesizeColorMeta = def.id === "colors";

  // Top-level namespaces (e.g. "button", "pill", "color", "icon").
  for (const ns of def.namespaces ?? []) {
    const nsVal = root[ns];
    if (!nsVal || typeof nsVal !== "object" || Array.isArray(nsVal)) continue;
    vars.push(...flattenNamespace(nsVal as Record<string, unknown>, ns, root, synthesizeColorMeta));
  }

  // token.* sub-keys (e.g. token.bg, token.fg, token.h1).
  const tokenNs = root.token;
  if (tokenNs && typeof tokenNs === "object" && !Array.isArray(tokenNs)) {
    const joiner = def.tokenSubKeyJoiner ?? ".";
    const aliases = def.tokenSubKeyAliases ?? {};
    for (const subKey of def.tokenSubKeys ?? []) {
      const subVal = (tokenNs as Record<string, unknown>)[subKey];
      if (!subVal || typeof subVal !== "object" || Array.isArray(subVal)) continue;
      const flatVars = flattenNamespace(
        subVal as Record<string, unknown>,
        `token.${subKey}`,
        root,
        synthesizeColorMeta,
      );

      // Remap keys when a non-default joiner or alias is in effect.
      const outputKey = aliases[subKey] ?? subKey;
      const needsRemap = joiner !== "." || outputKey !== subKey;
      if (needsRemap) {
        const oldPrefix = `token.${subKey}.`;
        const newPrefix = joiner === "-" ? `token.${outputKey}-` : `token.${outputKey}.`;
        vars.push(
          ...flatVars.map((v) =>
            v.key.startsWith(oldPrefix)
              ? { ...v, key: newPrefix + v.key.slice(oldPrefix.length) }
              : v,
          ),
        );
      } else {
        vars.push(...flatVars);
      }
    }
  }

  // brand.* sub-keys (e.g. brand.logo, brand.shape).
  const brandNs = root.brand;
  if (brandNs && typeof brandNs === "object" && !Array.isArray(brandNs)) {
    for (const subKey of def.brandSubKeys ?? []) {
      const subVal = (brandNs as Record<string, unknown>)[subKey];
      if (!subVal || typeof subVal !== "object" || Array.isArray(subVal)) continue;
      vars.push(...flattenNamespace(subVal as Record<string, unknown>, `brand.${subKey}`, root));
    }
  }

  return vars;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the path to `tokens.json` for a given workspace root.
 */
export function tokensJsonPath(workspaceRoot: string): string {
  return path.join(workspaceRoot, ".appostle", "brand", "tokens.json");
}

/**
 * Returns true when `tokens.json` exists for the given workspace root.
 */
export async function tokensJsonExists(workspaceRoot: string): Promise<boolean> {
  try {
    await fs.access(tokensJsonPath(workspaceRoot));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads `tokens.json` and returns exactly the 10 legacy sections as
 * `BrandEntry[]`, each mapped via the explicit remapping table above.
 *
 * Section IDs and names match the original `.md` filenames (without extension)
 * so the sidebar shows the same 10 items that existed before migration.
 */
export async function listBrandsFromTokensJson(
  workspaceRoot: string,
  scope: BrandScope = "project",
): Promise<BrandEntry[]> {
  const filePath = tokensJsonPath(workspaceRoot);

  let stat: { mtime: Date; size: number };
  try {
    const s = await fs.stat(filePath);
    stat = { mtime: s.mtime, size: s.size };
  } catch {
    return [];
  }

  let root: Record<string, unknown>;
  try {
    const text = await fs.readFile(filePath, "utf8");
    root = JSON.parse(text) as Record<string, unknown>;
  } catch {
    return [];
  }

  const modifiedAt = stat.mtime.toISOString();
  const proseDir = path.join(workspaceRoot, ".appostle", "brand", "prose");

  const entries: BrandEntry[] = await Promise.all(
    SECTION_DEFS.map(async (def) => {
      const variables = buildSectionVariables(def, root);
      let body = "";

      // Read prose/<section>.md body if present.
      try {
        const proseText = await fs.readFile(path.join(proseDir, `${def.id}.md`), "utf8");
        const match = /^---[\s\S]*?---\r?\n([\s\S]*)$/.exec(proseText);
        body = match ? match[1].trim() : proseText.trim();
      } catch {
        // No prose file for this section — body stays empty.
      }

      return {
        id: `${scope}:${def.id}`,
        scope,
        name: def.id,
        description: "",
        variables,
        path: filePath,
        modifiedAt,
        size: stat.size,
        body,
      };
    }),
  );

  return entries;
}
